import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { TemplateFieldsDialog } from "@/components/features/settings/defaults/matter-templates/TemplateFieldsDialog";
import { useCreateFirmFile } from "@/lib/data/react-query";
import { VITE_APRYSE_LICENSE_KEY } from "@/lib/data/static/importEnv";
import {
  ensureFileNameHasExtension,
  getFileNameParts,
} from "@/lib/utils/matterFile";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import {
  DocxEditor,
  DOCX_MIME_TYPE,
  useDocxTemplateFieldActions,
  type DocxEditorHandle,
} from "@/components/features/matter/file-repo/DocxEditor";

const createDocxSchema = z.object({
  fileName: z.string().trim().min(1, "File name is required"),
});

const normalizeDocxFileName = (name: string) => {
  const trimmed = name.trim();
  if (!trimmed) return "";
  return ensureFileNameHasExtension(trimmed, "docx");
};

interface CreateDocxFirmFileModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  currentFolderId?: string;
  existingFileNames?: string[];
}

export const CreateDocxFirmFileModal = ({
  open,
  setOpen,
  currentFolderId = "",
  existingFileNames = [],
}: CreateDocxFirmFileModalProps) => {
  const createFirmFile = useCreateFirmFile();
  const editorRef = useRef<DocxEditorHandle | null>(null);
  const {
    templateFieldsTriggerRef,
    handleInsertAtCursor,
    handleOpenTemplateFields,
  } = useDocxTemplateFieldActions(editorRef);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const form = useForm<z.infer<typeof createDocxSchema>>({
    resolver: zodResolver(createDocxSchema),
    defaultValues: {
      fileName: "New document",
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!open) {
      form.reset({ fileName: "New document" });
      setIsEditorReady(false);
      setIsExporting(false);
    }
  }, [form, open]);

  const isSaving = createFirmFile.isPending || isExporting;

  const handleSave = form.handleSubmit(async (data) => {
    if (!editorRef.current) {
      toast.error("Document editor is not ready yet.");
      return;
    }

    const normalizedFileName = normalizeDocxFileName(data.fileName);
    if (!normalizedFileName) {
      form.setError("fileName", {
        type: "manual",
        message: "File name is required",
      });
      return;
    }

    const existingNames = new Set(
      existingFileNames.map((name) => name.toLowerCase()),
    );
    if (existingNames.has(normalizedFileName.toLowerCase())) {
      form.setError("fileName", {
        type: "manual",
        message: "A file with this name already exists",
      });
      return;
    }

    setIsExporting(true);
    try {
      const blob = await editorRef.current.exportDocument();
      if (!blob) {
        toast.error("Unable to export the document.");
        return;
      }

      const file = new File([blob], normalizedFileName, {
        type: DOCX_MIME_TYPE,
      });

      await createFirmFile.mutateAsync({
        name: getFileNameParts(normalizedFileName).fileName,
        idLocation: currentFolderId,
        isFolder: false,
        file,
      });

      setOpen(false);
    } catch (error) {
      console.error("Failed to create document", error);
      toast.error("Failed to create document");
    } finally {
      setIsExporting(false);
    }
  });

  const formId = "create-docx-firm-file-form";

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title="Create file"
      description="Create a new firm file."
      isDirty={form.formState.isDirty}
      bodyContained
      className="top-0 left-0 h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none translate-x-0 translate-y-0 rounded-none"
      footer={
        <div className="flex w-full justify-end gap-x-2">
          <FormCancelButton
            setOpen={setOpen}
            isDirty={form.formState.isDirty}
          />
          <Button
            type="submit"
            form={formId}
            variant="light-primary"
            disabled={!isEditorReady || isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      }
    >
      <Form {...form}>
        <form
          id={formId}
          className="relative flex h-full min-h-0 grow flex-col overflow-hidden"
          onSubmit={handleSave}
        >
          {isSaving && <FormLoadingOverlay />}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex min-h-full flex-col gap-4 px-4 py-4">
              <div className="flex flex-wrap items-end gap-3">
                <FormField
                  control={form.control}
                  name="fileName"
                  render={({ field }) => (
                    <FormItem className="max-w-sm flex-1">
                      <div className="flex">
                        <FormLabel>File name</FormLabel>
                        <FormMessage
                          data-testid="error-message"
                          className="grow text-right"
                        />
                      </div>
                      <FormControl>
                        <div className="flex items-center">
                          <Input placeholder="New document" {...field} />
                          <span className="text-muted-foreground ml-2 text-sm">
                            .docx
                          </span>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <TemplateFieldsDialog
                  hideTrigger
                  onInsertAtCursor={handleInsertAtCursor}
                  triggerLabel="Template variables"
                  triggerRef={templateFieldsTriggerRef}
                />
              </div>
              <DocxEditor
                ref={editorRef}
                fileName="New document.docx"
                presignedUrlKey=""
                fileType="firm-file"
                licenseKey={VITE_APRYSE_LICENSE_KEY}
                loadBlankDocument
                onTemplateFieldsRequest={handleOpenTemplateFields}
                onReadyChange={setIsEditorReady}
                isActive={open}
                showSaveButton={false}
                showHeader={false}
                className="mt-2 min-h-[600px] flex-1"
              />
            </div>
          </div>
        </form>
      </Form>
    </AppModal>
  );
};
